/**
 * Traduction entre les contrôles structurés de la fiche et la chaîne qui est stockée.
 *
 * La fiche enfant garde **une chaîne par champ**, historisée et chaînée côté serveur. Les
 * nouveaux contrôles — étiquettes, rangs répétables, duos — ne changent rien à cela : ils
 * structurent la saisie, pas le stockage. Aucune migration, aucun champ nouveau, et
 * l'export comme l'historique continuent de lire ce qu'ils lisaient.
 *
 * La contrepartie est ici : il faut relire une chaîne écrite avant l'existence de ces
 * contrôles, ou à la main. La règle qui gouverne tout ce fichier est donc **ne jamais
 * deviner** : ce qui ne se relit pas proprement rend `null`, et l'écran repasse alors ce
 * champ en texte libre avec sa valeur intacte. Une allergie mal découpée vaudrait mieux
 * que perdue, mais une allergie *inventée* par un découpage optimiste serait pire que les
 * deux — c'est une donnée qu'on lit aux urgences.
 */

/** Entre deux rangs. Le point-virgule espacé est rare dans une posologie ou un prénom. */
export const SEPARATEUR_RANG = ' ; ';

/** Entre deux cellules d'un même rang. */
export const SEPARATEUR_CELLULE = ' — ';

/**
 * Relit une valeur en rangs de `colonnes` cellules.
 *
 * Rend `null` — et non un résultat approché — dès qu'un rang ne tient pas dans le nombre
 * de colonnes attendu. C'est le signal que la valeur vient d'ailleurs : l'écran la rendra
 * en texte libre plutôt que de la redécouper.
 */
export function lireRangs(valeur: string | null | undefined, colonnes: number): string[][] | null {
  const brut = valeur ?? '';
  if (brut.trim() === '') return [];

  // Pas de `trim()` sur la chaîne entière avant le découpage. Une première cellule laissée
  // vide s'écrit « ␣—␣tante » : rogner l'espace de tête ampute le séparateur, et la
  // relecture rendait une ligne dont la première cellule valait « — tante ». Le nom d'une
  // personne autorisée au retrait se réécrivait ainsi tout seul, sans que rien ne le dise.
  // Les cellules, elles, sont trimées une à une plus bas : les espaces d'une saisie
  // humaine sont absorbés là, où ils ne détruisent rien.

  const rangs: string[][] = [];
  for (const morceau of brut.split(SEPARATEUR_RANG)) {
    const cellules = morceau.split(SEPARATEUR_CELLULE).map((c) => c.trim());
    if (cellules.length > colonnes) return null;
    // Un rang plus court est complété : c'est le cas d'une dernière colonne laissée vide,
    // qui ne s'écrit pas. Un rang plus long, lui, veut dire que le texte contenait déjà
    // le séparateur — on ne sait alors plus où sont les colonnes.
    while (cellules.length < colonnes) cellules.push('');
    rangs.push(cellules);
  }
  return rangs;
}

/**
 * Écrit des rangs.
 *
 * Un rang entièrement vide disparaît — c'est la ligne qu'on a ajoutée puis renoncé à
 * remplir. Les cellules vides de fin sont retirées pour que « Judo — Mercredi — » ne
 * s'enregistre pas avec un tiret orphelin.
 */
export function ecrireRangs(rangs: readonly (readonly string[])[]): string {
  return rangs
    .map((rang) => {
      const cellules = rang.map((c) => c.trim());
      while (cellules.length > 0 && cellules[cellules.length - 1] === '') cellules.pop();
      return cellules.join(SEPARATEUR_CELLULE);
    })
    .filter((rang) => rang !== '')
    .join(SEPARATEUR_RANG);
}

/** Relit une liste d'étiquettes séparées par des virgules. Toujours lisible, jamais `null`. */
export function lireEtiquettes(valeur: string | null | undefined): string[] {
  return (valeur ?? '')
    .split(',')
    .map((e) => e.trim())
    .filter((e) => e !== '');
}

export function ecrireEtiquettes(etiquettes: readonly string[]): string {
  return etiquettes
    .map((e) => e.trim())
    .filter((e) => e !== '')
    .join(', ');
}
